import {
  type FullAssessmentSectionId,
  type FullAssessmentSectionResult,
  FullAssessmentSectionStatus,
} from '@azymmetric/shared/types/full-assessment.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Spinner } from '@components/shared/spinner.component';
import { AccordionContent, AccordionItem } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs';
import { Textarea } from '@components/ui/textarea';
import { cn } from '@lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CheckIcon, ChevronRightIcon, MessageSquareTextIcon, SparklesIcon, XIcon } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import { FULL_ASSESSMENT_SECTION_LABELS, isSectionAcceptable } from './full-assessment.util';

type FullAssessmentSectionCardProps = {
  /**
   * Renders the validated draft payload for this section (names resolved by the
   * panel's catalog data). Null falls back to a generic placeholder.
   */
  draftContent: ReactNode;
  isAccepted: boolean;
  /**
   * The draft is structurally identical to the current form content (e.g.
   * right after an Accept) — there is nothing to compare, so the
   * Proposed | Current toggle is hidden.
   */
  isDraftSameAsCurrent: boolean;
  /**
   * The current draft was explicitly rejected: the card falls back to the
   * pre-draft state (seed or empty) until a new draft supersedes it.
   */
  isRejected: boolean;
  /**
   * Retry was clicked while a generation was in flight; it dispatches
   * automatically once the stream finishes (click again to cancel).
   */
  isRetryQueued: boolean;
  /**
   * This section is being (re)generated right now.
   */
  isStreaming: boolean;
  onAccept: () => void;
  onAddComment: (comment: { quote: string; text: string }) => void;
  /**
   * Expand this section's accordion (the composer opens inside the content).
   */
  onExpand: () => void;
  onReject: () => void;
  onRetry: () => void;
  result?: FullAssessmentSectionResult;
  sectionId: FullAssessmentSectionId;
  /**
   * Current form content rendered through the same UI as generated drafts;
   * shown until a draft supersedes it and again after a draft is rejected.
   */
  seedContent?: ReactNode;
};

// Resolution badges only (Draft / Accepted / Rejected / Failed) — progress
// states (Generating / Queued) render in the actions slot, replacing the
// Regenerate control they make unavailable.
const SectionStatusBadge: FC<{
  isAccepted: boolean;
  isRejected: boolean;
  result?: FullAssessmentSectionResult;
}> = (props) => {
  const { isAccepted, isRejected, result } = props;

  if (result?.status === FullAssessmentSectionStatus.FAILED) {
    return (
      <Badge
        size="xs"
        variant="destructive"
      >
        Failed
      </Badge>
    );
  }

  if (result?.status === FullAssessmentSectionStatus.COMPLETED) {
    if (isAccepted) {
      return (
        <Badge
          size="xs"
          variant="active"
        >
          <CheckIcon size={12} />
          Accepted
        </Badge>
      );
    }

    if (isRejected) {
      return (
        <Badge
          size="xs"
          variant="default"
        >
          Rejected
        </Badge>
      );
    }

    return (
      <Badge
        size="xs"
        variant="primary"
      >
        Draft
      </Badge>
    );
  }

  return null;
};

export const FullAssessmentSectionCard: FC<FullAssessmentSectionCardProps> = (props) => {
  const {
    draftContent,
    isAccepted,
    isDraftSameAsCurrent,
    isRejected,
    isRetryQueued,
    isStreaming,
    onAccept,
    onAddComment,
    onExpand,
    onReject,
    onRetry,
    result,
    sectionId,
    seedContent,
  } = props;

  const bodyRef = useRef<HTMLDivElement>(null);
  const [commentQuote, setCommentQuote] = useState('');
  const [commentText, setCommentText] = useState('');
  // Controlled compare view: the tooltip's `asChild` trigger overwrites the
  // Tabs trigger's data-state attribute, so active styling cannot rely on it.
  const [compareView, setCompareView] = useState<'current' | 'proposed'>('proposed');
  const [isComposerOpen, setIsComposerOpen] = useState(false);

  const draftLogId = result?.logId;
  const previousDraftLogIdRef = useRef(draftLogId);

  // A new draft version resets the comparison back to the proposal.
  useEffect(() => {
    if (previousDraftLogIdRef.current === draftLogId) return;

    previousDraftLogIdRef.current = draftLogId;
    setCompareView('proposed');
  }, [draftLogId]);

  const label = FULL_ASSESSMENT_SECTION_LABELS[sectionId];
  const isCompleted = result?.status === FullAssessmentSectionStatus.COMPLETED;
  const isFailed = result?.status === FullAssessmentSectionStatus.FAILED;
  const isGenerating = isStreaming || result?.status === FullAssessmentSectionStatus.STREAMING;
  const isServerQueued = result?.status === FullAssessmentSectionStatus.QUEUED;
  // A rejected draft renders like the pre-draft state until a new one lands.
  const hasActiveDraft = isCompleted && !isRejected;
  // Finished sections stay interactive while other sections are still
  // generating: accepting/rejecting and staging edits are local-only actions,
  // and Retry queues until the in-flight stream finishes. A queued retry is a
  // pending discard of the current draft, so resolving it makes no sense.
  const canResolve = isSectionAcceptable(result) && !isAccepted && !isRejected && !isStreaming && !isRetryQueued;
  const hasSeedContent = seedContent !== null && seedContent !== undefined;
  const showCompareTabs = hasSeedContent && !isDraftSameAsCurrent;
  const showSeed = !hasActiveDraft && !isFailed && hasSeedContent;

  // A selection that ends inside this card becomes the comment's quoted
  // excerpt, so suggest-edits carry their section deterministically.
  // See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D10).
  const openComposer = () => {
    if (isComposerOpen) {
      setIsComposerOpen(false);
      return;
    }

    const selection = window.getSelection();
    const selectedText = selection && !selection.isCollapsed ? selection.toString().trim() : '';
    const isSelectionInCard =
      selectedText && selection?.focusNode ? Boolean(bodyRef.current?.contains(selection.focusNode)) : false;

    setCommentQuote(isSelectionInCard ? selectedText.replace(/\s*\n+\s*/g, ' — ') : '');
    setCommentText('');
    setIsComposerOpen(true);
    onExpand();
  };

  const submitComment = () => {
    const text = commentText.trim();
    if (!text) return;

    onAddComment({ quote: commentQuote || label, text });
    setIsComposerOpen(false);
    setCommentText('');
  };

  return (
    <AccordionItem
      className={cn(
        'rounded-lg border bg-white px-3 transition-colors last:border-b',
        isFailed ? 'border-red-200' : isAccepted ? 'border-green-200' : 'border-gray-200',
      )}
      data-section-id={sectionId}
      value={sectionId}
    >
      <AccordionPrimitive.Header className="flex items-center justify-between gap-2">
        <AccordionPrimitive.Trigger
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 py-2.5 text-left',
            '[&>svg:first-child]:transition-transform [&>svg:first-child]:duration-200 [&[data-state=open]>svg:first-child]:rotate-90',
          )}
        >
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
          <h4 className="truncate text-sm font-medium text-gray-800">{label}</h4>
          <SectionStatusBadge
            isAccepted={isAccepted}
            isRejected={isRejected}
            result={result}
          />
        </AccordionPrimitive.Trigger>

        <div className="flex shrink-0 items-center gap-1">
          {hasActiveDraft && !isGenerating && !isRetryQueued && (
            <TooltipComponent
              side="top"
              trigger={
                <IconButton
                  ariaLabel="Suggest Edit"
                  icon={MessageSquareTextIcon}
                  onClick={openComposer}
                  size="sm"
                  type="button"
                  variant="text"
                />
              }
            >
              Suggest Edit
            </TooltipComponent>
          )}
          {isGenerating ? (
            <Badge
              size="xs"
              variant="default"
            >
              <Spinner size="sm" />
              Generating
            </Badge>
          ) : isServerQueued ? (
            <Badge
              size="xs"
              variant="default"
            >
              Generation Queued
            </Badge>
          ) : isRetryQueued ? (
            <TooltipComponent
              side="top"
              trigger={
                <button
                  className="cursor-pointer"
                  onClick={onRetry}
                  type="button"
                >
                  <Badge
                    size="xs"
                    variant="default"
                  >
                    Generation Queued
                    <XIcon size={12} />
                  </Badge>
                </button>
              }
            >
              Generation queued — runs when the current generation finishes. Click to cancel.
            </TooltipComponent>
          ) : (
            <TooltipComponent
              side="top"
              trigger={
                <IconButton
                  ariaLabel="Regenerate section"
                  icon={SparklesIcon}
                  onClick={onRetry}
                  size="sm"
                  type="button"
                  variant="text"
                />
              }
            >
              Regenerate
            </TooltipComponent>
          )}
          {canResolve && (
            <TooltipComponent
              side="top"
              trigger={
                <IconButton
                  ariaLabel="Reject draft"
                  icon={XIcon}
                  onClick={onReject}
                  size="sm"
                  type="button"
                  variant="destructive"
                />
              }
            >
              Reject
            </TooltipComponent>
          )}
          {canResolve && (
            <TooltipComponent
              side="top"
              trigger={
                <IconButton
                  ariaLabel="Accept into form"
                  icon={CheckIcon}
                  onClick={onAccept}
                  size="sm"
                  type="button"
                  variant="primary"
                />
              }
            >
              Accept
            </TooltipComponent>
          )}
        </div>
      </AccordionPrimitive.Header>

      <AccordionContent className="pb-3">
        <div ref={bodyRef}>
          {isFailed && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {result?.errorMessage || 'This section failed to generate. Please retry.'}
            </div>
          )}

          {hasActiveDraft &&
            (showCompareTabs ? (
              // Proposed | Current toggle — rendered only when the form holds
              // content that actually differs from the draft. Both views use
              // the same renderers. The selection is controlled state (not
              // data-state styling) because the tooltip trigger clobbers the
              // Tabs data-state attribute via asChild.
              <Tabs
                onValueChange={(value) => setCompareView(value === 'current' ? 'current' : 'proposed')}
                value={compareView}
              >
                <TabsList className="h-7 gap-0.5 rounded-md bg-gray-100 p-0.5">
                  <TooltipComponent
                    side="top"
                    trigger={
                      <TabsTrigger
                        className={cn(
                          'rounded-sm px-2.5 py-0.5 text-xs font-medium shadow-none',
                          compareView === 'proposed'
                            ? 'bg-selected-surface text-selected-foreground'
                            : 'bg-transparent text-gray-500 hover:text-gray-700',
                        )}
                        value="proposed"
                      >
                        Proposed
                      </TabsTrigger>
                    }
                  >
                    The newly generated draft for this section
                  </TooltipComponent>
                  <TooltipComponent
                    side="top"
                    trigger={
                      <TabsTrigger
                        className={cn(
                          'rounded-sm px-2.5 py-0.5 text-xs font-medium shadow-none',
                          compareView === 'current'
                            ? 'bg-selected-surface text-selected-foreground'
                            : 'bg-transparent text-gray-500 hover:text-gray-700',
                        )}
                        value="current"
                      >
                        Current
                      </TabsTrigger>
                    }
                  >
                    What the assessment form holds right now
                  </TooltipComponent>
                </TabsList>
                <TabsContent value="proposed">
                  <div className="text-xs text-gray-700">{draftContent}</div>
                </TabsContent>
                <TabsContent value="current">
                  <div className="text-xs text-gray-700">{seedContent}</div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-xs text-gray-700">{draftContent}</div>
            ))}

          {result?.status === FullAssessmentSectionStatus.STREAMING && result.text !== undefined && (
            <div className="whitespace-pre-wrap text-xs text-gray-600">{result.text}</div>
          )}

          {showSeed && <div className="text-xs text-gray-700">{seedContent}</div>}

          {!hasActiveDraft && !isFailed && !isStreaming && !showSeed && (
            <p className="text-xs text-gray-400">Empty — will be drafted by the full generation.</p>
          )}

          {isComposerOpen && (
            <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-2">
              {commentQuote && <p className="text-xs italic text-gray-500">“{commentQuote}”</p>}
              <Textarea
                autoFocus
                className="min-h-16 bg-white text-sm"
                onChange={(event) => setCommentText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    submitComment();
                  }
                  if (event.key === 'Escape') {
                    setIsComposerOpen(false);
                  }
                }}
                placeholder="Make the threat analysis more specific about EW countermeasures…"
                value={commentText}
              />
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setIsComposerOpen(false)}
                  size="xs"
                  type="button"
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!commentText.trim()}
                  onClick={submitComment}
                  size="xs"
                  type="button"
                  variant="primary"
                >
                  Suggest Edit
                </Button>
              </div>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
